import React, { useEffect, useMemo, useState } from 'react';
import { Text, VStack } from '@chakra-ui/react';
import { DocumentWithDownloadUrl as Document, SourceOut } from '@/client';
import usePresignedDownloadUrls from '../../../../../hooks/usePresignedDownloadUrls.ts';
import { isPdf } from '@/utils/documentUtils.ts';
import RecordPreviewItem from '../RecordPreviewItem/RecordPreviewItem';
import { getSortedUniquePages, getSourceKey } from '../citationUtils.ts';
import { makeLogger } from '@/utils/logger/logger';
import SourceFullscreenPreview from './SourceFullscreenPreview.tsx';

const logger = makeLogger('annotations-list');

interface AnnotationsListProps {
	/** Cited sources — one per document, carrying the cited page(s). */
	sources: SourceOut[];
	documents: Document[];
	highlightedAnnotationIndex?: number | null;
	registerAnnotationRef: (index: number, element: HTMLElement | null) => void;
	onOpenDocument: (doc: Document, pages?: number[]) => void;
}

interface FullscreenSourceRequest {
	document: Document;
	page: number;
	requestId: number;
}

const ChatResponseAnnotationsList: React.FC<AnnotationsListProps> = (props: AnnotationsListProps) => {
	const { sources, documents, highlightedAnnotationIndex, registerAnnotationRef, onOpenDocument } = props;
	const { fetchBatchDownloadUrls, downloadUrls } = usePresignedDownloadUrls();
	const [fullscreenSourceRequest, setFullscreenSourceRequest] = useState<FullscreenSourceRequest | null>(null);

	const documentsById = useMemo(() => new Map(documents.map((document) => [document.id, document])), [documents]);

	// Fetch presigned URLs for every referenced document.
	// TODO: presigned download URLs are still fetched here even when the user lacks
	// HOUSE_DOCUMENT_DOWNLOAD (the link is only hidden at render time in RecordPreviewItem).
	// The real gate belongs in the backend / data layer.
	useEffect(() => {
		const recordIds = sources
			.map((source) => (source.document_id != null ? parseInt(source.document_id, 10) : null))
			.filter((id): id is number => id !== null && !Number.isNaN(id) && documentsById.has(id));
		const ids = recordIds.filter((id) => !downloadUrls[id]);
		if (ids.length > 0) {
			fetchBatchDownloadUrls(ids, documents);
		}
	}, [sources, documents, documentsById]);

	logger.debug(
		'docs:',
		documents.map((d) => d.filename),
		'| sources:',
		sources.map((s) => s.source_file),
	);

	if (sources.length === 0) {
		return <Text textStyle="body.sm" color="fg.muted" fontStyle="italic" />;
	}

	return (
		<VStack mt={4} gap={3} align="stretch">
			{sources.map((source, index) => {
				const recordId = source.document_id != null ? parseInt(source.document_id, 10) : null;
				const doc = recordId ? documentsById.get(recordId) : undefined;
				const url = recordId ? (downloadUrls[recordId] ?? doc?.download_url ?? undefined) : undefined;
				const pages = getSortedUniquePages(source.pages ?? []);
				return (
					<RecordPreviewItem
						key={getSourceKey(source)}
						index={index}
						label={source.source_file ?? source.document_id ?? 'Source'}
						doc={doc}
						url={url}
						pages={pages}
						onOpenDocument={onOpenDocument}
						onOpenPage={(page) => {
							if (!doc || !url || !isPdf(doc)) return;
							setFullscreenSourceRequest((current) => ({
								document: { ...doc, download_url: url },
								page,
								requestId: (current?.requestId ?? 0) + 1,
							}));
						}}
						isHighlighted={highlightedAnnotationIndex === index}
						highlightedAnnotationIndex={highlightedAnnotationIndex}
						registerAnnotationRef={registerAnnotationRef}
					/>
				);
			})}
			{fullscreenSourceRequest && (
				<SourceFullscreenPreview
					key={fullscreenSourceRequest.requestId}
					document={fullscreenSourceRequest.document}
					page={fullscreenSourceRequest.page}
					onClose={() => setFullscreenSourceRequest(null)}
				/>
			)}
		</VStack>
	);
};

export default ChatResponseAnnotationsList;
